import { cn } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import type { Table } from "@tanstack/react-table";
import { ArrowRight, Cable, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ExportHistoryType } from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import { useNavigateToInvoiceWithBrowsingQueue } from "@/hooks/useNavigateToInvoiceWithBrowsingQueue";
import { useGetAllDatevTokens } from "@/queries/datev-tokens/get-all";

import { UploadInvoiceButton } from "@/components/shared/buttons/upload-button";
import { ComboBoxInput } from "@/components/shared/form/combo-box-input";
import { MultiSelectInput } from "@/components/shared/form/multi-select-input";
// TODO: fix this and move to shared components
import { DatevExportButton } from "@/features/invoice-overview/export/datev-export-button";
import { useExportInitialQuery } from "@/features/todos/initial-filters";
import { usePermissions } from "@/hooks/usePermissions";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { InvoiceType } from "@/lib/constants/invoice-type";
import { formatTimestampToString } from "@/lib/dates/dateUtils";
import { useTenant } from "@/providers/session";
import { useInvoiceLiveCountsSubscription } from "@/queries/invoices/live-counts/live-counts";
import { useMemo } from "react";
import FiltersRoot from "../data-table/filters";
import { GlobalFilterInput } from "../data-table/filters/inputs/global-filter";
import { StatusInputFilter } from "./filter/statusFilter";
import { SpecialFilterValue } from "./filter/utils";

type Props = {
  data: any;
  exportHistory: ExportHistoryType[];
  table: Table<any>;
  filtersConfig: {
    showStatusFilter?: boolean;
    showAccountFilter?: boolean;
    showSupplierFilter?: boolean;
    showTypeFilter?: boolean;
    showExportsFilter?: boolean;
    showUploadButton?: boolean;
    showOcrReviewedFilter?: boolean;
    showExportAllButton?: boolean;
    autoFocus?: boolean;
  };
  showStartReviewButton?: boolean;
  filters: any;
  setFilters: any;
  globalSearch: string;
  setGlobalSearch: (value: string) => void;
  invoiceType?: InvoiceTypeString;
};

export const InvoicesFilters = ({
  data,
  table,
  exportHistory,
  filtersConfig: {
    showStatusFilter = true,
    showAccountFilter = true,
    showSupplierFilter = true,
    showTypeFilter = false,
    showExportsFilter = true,
    showUploadButton = true,
    showOcrReviewedFilter = false,
    showExportAllButton = false,
    autoFocus = false,
  },
  showStartReviewButton = false,
  filters,
  setFilters,
  globalSearch,
  setGlobalSearch,
  invoiceType,
}: Props) => {
  const { isSuperAdmin } = usePermissions();
  const { tokens } = useGetAllDatevTokens();
  const token = tokens?.[0];

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companySlug } = useTenant();
  const navigateToInvoice = useNavigateToInvoiceWithBrowsingQueue(table);

  const handleDatevSetupClick = () => {
    navigate({
      to: "/$company/buchhaltung/einstellungen/integrationen",
      params: { company: companySlug },
    });
  };

  const getSupplierConfigItems = () => {
    if (data?.invoiceOverviewConfig?.suppliers) {
      const suppliers = data?.invoiceOverviewConfig?.suppliers.map(
        (supplier: any) => ({
          label: supplier.supplierName,
          value: supplier.supplierId,
          subLabel: supplier.datevAccountNumber,
        }),
      );
      if (invoiceType === undefined) {
        suppliers.push({
          label: "Unbekannt",
          value: SpecialFilterValue.Unknown,
          subLabel: t("suppliers.creditors_slash_debtors"),
        });
      } else {
        if (invoiceType === "receivable") {
          suppliers.push({
            label: "Unbekannt",
            value: SpecialFilterValue.Unknown,
            subLabel: "Debitor",
          });
        } else {
          suppliers.push({
            label: "Unbekannt",
            value: SpecialFilterValue.Unknown,
            subLabel: "Kreditor",
          });
        }
      }
      return suppliers;
    }
  };

  const getAccountsConfig = () => {
    if (data?.invoiceOverviewConfig?.accounts) {
      const accounts = data?.invoiceOverviewConfig?.accounts.map(
        (account: any) => ({
          label: account.accountName,
          value: account.accountId,
          subLabel: account.accountNo,
        }),
      );
      accounts.push({
        label: "Unbekannt",
        value: SpecialFilterValue.Unknown,
        subLabel: "Sachkonto",
      });
      return accounts;
    }
  };

  const getInvoiceTypeConfig = () => {
    return [
      {
        label: t("invoices-table.invoice_type.payable"),
        value: InvoiceType.PAYABLE,
        alternativeLabels: [],
      },
      {
        label: t("invoices-table.invoice_type.receivable"),
        value: InvoiceType.RECEIVABLE,
        alternativeLabels: [],
      },
    ];
  };

  const handleSubmit = (_filters: any) => {
    setFilters(_filters);
  };

  const selectedRows = table?.getFilteredSelectedRowModel().rows;

  const selectedInvoiceIds = useMemo(() => {
    return selectedRows?.map((row) => row.original.invoice.invoiceId) ?? [];
  }, [selectedRows]);

  return (
    // FIXME: this hardcoded minimum height is added because filters are not visible in the initial UI breaking the table layout.
    <div className="flex flex-col gap-2 min-h-[88px]">
      <div className="flex items-center gap-2">
        <GlobalFilterInput
          placeholder={t("invoices_search_placeholder")}
          value={globalSearch}
          onChange={setGlobalSearch}
          className="max-w-sm"
          autoFocus={autoFocus}
        />
        {showUploadButton && <UploadInvoiceButton />}
      </div>
      <FiltersRoot onSubmit={handleSubmit} values={filters}>
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2">
            {showTypeFilter && (
              <div className="w-32">
                <ComboBoxInput
                  name="type"
                  options={getInvoiceTypeConfig()}
                  placeholder={t("invoices-table.invoice_type.title")}
                  enableClear
                  highlightWhenSelected
                />
              </div>
            )}
            {showSupplierFilter && (
              <div className="w-48">
                <ComboBoxInput
                  name="supplier_id"
                  options={getSupplierConfigItems() ?? []}
                  placeholder={
                    invoiceType === undefined
                      ? t("suppliers.creditors_slash_debtors")
                      : invoiceType === "receivable"
                        ? t("debtors.title")
                        : t("creditors.title")
                  }
                  enableClear
                  highlightWhenSelected
                />
              </div>
            )}
            <div className="w-48">
              {showAccountFilter && (
                <ComboBoxInput
                  name="accountId"
                  options={getAccountsConfig() ?? []}
                  placeholder={t("invoices-table.filters.account.placeholder")}
                  enableClear
                  highlightWhenSelected
                />
              )}
            </div>
            <div>{showStatusFilter && <StatusInputFilter />}</div>
            <div>
              {showExportsFilter && (
                <MultiSelectInput
                  mode="single"
                  name="datevExportId"
                  options={exportHistory.map((option) => {
                    const isNotBooked = option.stackName === "not_booked";

                    const getExportLabel = () => {
                      if (isNotBooked) {
                        return `${t("export_from")} ${formatTimestampToString(
                          option.exportedAt,
                        )} • ${t("todos.sort")}`;
                      }
                      return `${t("export_from")} ${formatTimestampToString(
                        option.exportedAt,
                      )} ${
                        option.stackName
                          ? `• ${t("stack")} ${option.stackName}`
                          : ""
                      }`;
                    };

                    return {
                      value: option.datevExportId,
                      label: getExportLabel(),
                    };
                  })}
                  placeholder={t(
                    "invoices-table.filters.export_history.placeholder",
                  )}
                  className={cn(
                    Array.isArray(filters?.datevExportId) &&
                      (filters?.datevExportId as unknown[]).length > 0
                      ? "bg-secondary"
                      : "text-muted-foreground",
                  )}
                />
              )}
            </div>
            {/* OCR reviewed filter intentionally hidden */}
          </div>
          {data && showExportAllButton && (token || isSuperAdmin) && (
            <ExportAllButton
              selectedInvoices={selectedInvoiceIds}
              table={table}
              invoiceType={invoiceType}
            />
          )}
          {data && showExportAllButton && !token && !isSuperAdmin && (
            <Button className="gap-2" onClick={handleDatevSetupClick}>
              {t("setup_datev_connection")}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
          {showStartReviewButton && table && (
            <Button
              className="px-4"
              onClick={() =>
                navigateToInvoice(
                  table.getRowModel().rows[0].original.invoice.invoiceId,
                )
              }
              disabled={table.getRowModel().rows.length === 0}
            >
              {t("payables.start_review")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </FiltersRoot>
    </div>
  );
};

const ExportAllButton = ({
  selectedInvoices,
  table,
  invoiceType,
}: {
  selectedInvoices: string[];
  table: Table<any>;
  invoiceType?: InvoiceTypeString;
}) => {
  const { t } = useTranslation();
  const { serverSideQuery } = useExportInitialQuery({
    type: invoiceType ?? null,
  });

  const { liveCounts, loading } = useInvoiceLiveCountsSubscription({
    query: serverSideQuery,
  });

  const exportAll = useMemo(() => {
    if (selectedInvoices.length > 0) {
      return false;
    }

    return true;
  }, [selectedInvoices]);

  const count = useMemo(() => {
    if (exportAll) {
      return liveCounts;
    }
    return selectedInvoices.length;
  }, [exportAll, liveCounts, selectedInvoices]);

  const countDisplay = useMemo(() => {
    if (selectedInvoices.length > 0) {
      // Selected state: "x von y" + "Belege ausgewählt"
      return {
        number: `${selectedInvoices.length} von ${liveCounts}`,
        label: t("documents_selected_label"),
      };
    }

    // Default state: "x" + "Belege"
    return {
      number: count.toString(),
      label: t("documents_label"),
    };
  }, [count, selectedInvoices.length, liveCounts, t]);

  const buttonLabel = useMemo(() => {
    if (selectedInvoices.length > 0) {
      return t("export_selection_button");
    }
    return t("export_all_button");
  }, [selectedInvoices.length, t]);

  const handleClearSelection = () => {
    table?.resetRowSelection(true);
  };

  return (
    <div className="flex flex-1 justify-end">
      <div className="flex items-center gap-2 rounded-lg border bg-background/50 px-[11px] py-[7px] shadow-sm">
        {selectedInvoices.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 -m-1 hover:bg-secondary/50"
            onClick={handleClearSelection}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
        <div className="flex flex-col items-center py-0.5">
          <span className="text-lg font-semibold leading-none">
            {countDisplay.number}
          </span>
          <span className="text-[11px] text-muted-foreground leading-tight mt-0.5">
            {countDisplay.label}
          </span>
        </div>
        <div className="h-6 w-px bg-border" />
        <DatevExportButton
          key={count}
          label={buttonLabel}
          showExportAllWarning={false}
          disable={loading || count === 0}
          invoiceType={invoiceType}
        />
      </div>
    </div>
  );
};
